import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert, Dropdown, ButtonGroup, Button, Badge, Row, Col } from 'react-bootstrap';
import { analyticsAPI } from '../api/analyticsAPI';
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI';
import SalesChart from '../components/SalesChart';
import SalesBarChart from '../components/SalesBarChart';

/**
 * SlowSellersWidget - Display slow selling items by metric type
 */
const SlowSellersWidget = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    // Chart type (pie or bar)
    const [chartType, setChartType] = useState('pie'); // Default: pie

    // Categories
    const [availableCategories, setAvailableCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    // Metric type options
    const metricTypes = [
        { value: 'quantity', label: 'Quantity' },
        { value: 'weight', label: 'Weight' },
        { value: 'jars', label: 'Jars' },
        { value: 'profit', label: 'Profit' }
    ];
    const [selectedMetricType, setSelectedMetricType] = useState('quantity'); // Default: quantity

    // Days options
    const daysOptions = [
        { value: 14, label: 'Last 14 Days' },
        { value: 30, label: 'Last 30 Days' },
        { value: 60, label: 'Last 60 Days' },
        { value: 90, label: 'Last 90 Days' }
    ];
    const [selectedDays, setSelectedDays] = useState(daysOptions[1]); // Default: 30 days

    // Top count options
    const topCountOptions = [
        { value: 10, label: 'Top 10' },
        { value: 15, label: 'Top 15' },
        { value: 20, label: 'Top 20' }
    ];
    const [selectedTopCount, setSelectedTopCount] = useState(topCountOptions[0]); // Default: Top 10

    // Comparison type options
    const comparisonOptions = [
        { value: 'previous_period', label: 'vs. Previous Period' },
        { value: '1_year', label: 'vs. 1 Year Ago' },
        { value: '2_years', label: 'vs. 2 Years Ago' },
        { value: '3_years', label: 'vs. 3 Years Ago' }
    ];
    const [selectedComparison, setSelectedComparison] = useState(comparisonOptions[0]); // Default: previous period

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await analyticsAPI.getSlowSellers(
                selectedMetricType,
                selectedDays.value,
                selectedTopCount.value,
                selectedCategories,
                selectedComparison.value
            );
            setData(result);
        } catch (err) {
            console.error('Error fetching slow sellers:', err);
            setError(err.response?.data?.error || 'Failed to load slow sellers data');
        } finally {
            setLoading(false);
        }
    };

    // Fetch available categories on mount and select all by default
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categories = await stockItemCategoryAPI.getAll();
                setAvailableCategories(categories || []);
                // Select all categories by default
                const allCategoryIds = (categories || []).map(cat => cat._id);
                setSelectedCategories(allCategoryIds);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCategories();
    }, []);

    // Fetch data when filters change
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMetricType, selectedDays.value, selectedTopCount.value, selectedCategories, selectedComparison.value]);

    // Toggle category selection
    const toggleCategory = (categoryId) => {
        setSelectedCategories((prevSelected) => {
            const numericCategoryId = Number(categoryId);
            const isCategorySelected = prevSelected.includes(numericCategoryId);
            return isCategorySelected
                ? prevSelected.filter(id => id !== numericCategoryId)
                : [...prevSelected, numericCategoryId];
        });
    };

    // Toggle between select all and deselect all
    const toggleAllCategories = () => {
        if (selectedCategories.length === availableCategories.length) {
            // All selected, so deselect all
            setSelectedCategories([]);
        } else {
            // Some or none selected, so select all
            const allCategoryIds = availableCategories.map(cat => cat._id);
            setSelectedCategories(allCategoryIds);
        }
    };

    return (
        <Card className="shadow-sm h-100">
            <Card.Header>
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                        <i className="material-icons me-2">trending_down</i>
                        <strong>Slow Sellers</strong>
                        <ButtonGroup size="sm">
                            {metricTypes.map((metric) => (
                                <Button
                                    key={metric.value}
                                    variant={selectedMetricType === metric.value ? 'primary' : 'outline-primary'}
                                    onClick={() => setSelectedMetricType(metric.value)}
                                    style={{ fontSize: '12px', padding: '2px 8px' }}
                                >
                                    {metric.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <ButtonGroup size="sm">
                            <Button
                                variant={chartType === 'pie' ? 'primary' : 'outline-primary'}
                                onClick={() => setChartType('pie')}
                                style={{ fontSize: '12px', padding: '2px 8px' }}
                            >
                                Pie
                            </Button>
                            <Button
                                variant={chartType === 'bar' ? 'primary' : 'outline-primary'}
                                onClick={() => setChartType('bar')}
                                style={{ fontSize: '12px', padding: '2px 8px' }}
                            >
                                Bar
                            </Button>
                        </ButtonGroup>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="light"
                                id="days-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                {selectedDays.label}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {daysOptions.map((option, index) => (
                                    <Dropdown.Item
                                        key={index}
                                        onClick={() => setSelectedDays(option)}
                                        active={option.value === selectedDays.value}
                                    >
                                        {option.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="light"
                                id="top-count-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                {selectedTopCount.label}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {topCountOptions.map((option, index) => (
                                    <Dropdown.Item
                                        key={index}
                                        onClick={() => setSelectedTopCount(option)}
                                        active={option.value === selectedTopCount.value}
                                    >
                                        {option.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="light"
                                id="comparison-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                {selectedComparison.label}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {comparisonOptions.map((option, index) => (
                                    <Dropdown.Item
                                        key={index}
                                        onClick={() => setSelectedComparison(option)}
                                        active={option.value === selectedComparison.value}
                                    >
                                        {option.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            title="Refresh"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                transition: 'all 0.2s',
                                opacity: loading ? 0.5 : 0.8
                            }}
                            onMouseEnter={(e) => !loading && (e.currentTarget.style.opacity = '1')}
                            onMouseLeave={(e) => !loading && (e.currentTarget.style.opacity = '0.8')}
                        >
                            <i className="material-icons" style={{ fontSize: '20px', color: '#6c757d' }}>
                                {loading ? 'hourglass_empty' : 'refresh'}
                            </i>
                        </button>
                    </div>
                </div>
            </Card.Header>
            <Card.Body>
                <Row>
                    {/* Categories Sidebar */}
                    <Col md={3}>
                        <div className="mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <strong style={{ fontSize: '14px' }}>Categories</strong>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={toggleAllCategories}
                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                >
                                    {selectedCategories.length === availableCategories.length ? 'Deselect All' : 'Select All'}
                                </Button>
                            </div>
                            <div
                                className="d-flex flex-wrap gap-2 p-2 border rounded"
                                style={{
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    backgroundColor: 'var(--bs-body-bg)'
                                }}
                            >
                                {availableCategories.map((category) => (
                                    <Badge
                                        key={category._id}
                                        bg={selectedCategories.includes(category._id) ? 'success' : 'light'}
                                        text={selectedCategories.includes(category._id) ? 'white' : 'dark'}
                                        style={{ cursor: 'pointer', fontSize: '12px' }}
                                        onClick={() => toggleCategory(category._id)}
                                    >
                                        {category.name}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </Col>

                    {/* Chart */}
                    <Col md={9}>
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="warning" />
                                <p className="text-muted mt-2">Loading data...</p>
                            </div>
                        ) : error ? (
                            <Alert variant="danger">
                                <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle' }}>
                                    error
                                </i>{' '}
                                {error}
                            </Alert>
                        ) : data && data.items && data.items.length > 0 ? (
                            chartType === 'pie' ? (
                                <SalesChart data={data.items} type="slow" metricUnit={data.metric_unit} />
                            ) : (
                                <SalesBarChart data={data.items} type="slow" metricUnit={data.metric_unit} />
                            )
                        ) : (
                            <Alert variant="info">
                                <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle' }}>
                                    info
                                </i>{' '}
                                No data available for the selected period.
                            </Alert>
                        )}
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default SlowSellersWidget;
